import React from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import ErrorLogs from '../ErrorLogs';

import { AddFileIcon, ProcessingIcon, SuccessIcon, ArrowRightIcon, DatabaseIcon, FailedIcon, ErrorIcon } from './Styles/Icons';
import { Container, ContainerHeading, ContainerBody, Title, Description, Actions, FlexRow, FlexColumn, Button, ButtonGroup, CircularProgress, ButtonWithLoader, FileUpload } from './Styles/BulkUpload';

import { updateErrorMessage, updateSuccessMessage, updateMessage, updateError } from '../../../actions/commonActions';
import { fetchSPJBatchJobs, fetchSPJBatchMetric, clearSPJFailed, downloadSPJErrors } from '../../../actions/spjActions';

import { SPJ_STATUS } from '../../../commons/utils';
import { UploadFileActions } from './Utils/index';

class BulkUpload extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			batchId: '',
			showErrorLogs: false,
			showErrorLogsButton: false,
			downloadingTemplate: false,
			heading: '',
			description: '',
		};
		this.startCheckingBatch = this.startCheckingBatch.bind(this);
	}

	async componentDidMount() {
		if (this.props.uploadFileBatch && this.props.uploadFileBatch !== '' && this.props.uploadFileType === this.props.spjAction) {
			this.setState({ batchId: this.props.uploadFileBatch }, () => {
				this.startCheckingBatch();
			});
		}
	}

	async componentWillUnmount() {
		this.stopCheckingBatch();
	}

	async componentDidUpdate(prevProps, prevState) {
		if (this.props.uploadFileBatch && this.state.batchId !== this.props.uploadFileBatch && this.props.uploadFileType === this.props.spjAction) {
			this.setState({ batchId: this.props.uploadFileBatch }, () => {
				this.startCheckingBatch();
			});
		}
	}

	startCheckingBatch = async () => {
		this.checkBatchInterval = setInterval(this.checkBatchHandler, 5000);
	};

	stopCheckingBatch = async () => {
		clearInterval(this.checkBatchInterval);
		this.clearInterval = null;
	};

	checkBatchHandler = async () => {
		const { batchId } = this.state;

		const batchJobsMetric = await this.props.fetchSPJBatchMetric(batchId);

		let pendingJobs = batchJobsMetric.filter((metric) => metric.status === SPJ_STATUS.PENDING && metric.event === this.props.spjAction);
		pendingJobs = pendingJobs[0] || {};
		let failedJobs = batchJobsMetric.filter((metric) => metric.status === SPJ_STATUS.FAILED && metric.event === this.props.spjAction);
		failedJobs = failedJobs[0] || {};

		if (pendingJobs && pendingJobs.count) {
			this.setState({
				heading: 'Processing your uploaded file',
				description: (pendingJobs.count || 0) === 1 ? `${pendingJobs.count || 0} item remaining.` : `${pendingJobs.count || 0} items remaining.`,
			});
		} else {
			this.stopCheckingBatch();

			if (failedJobs && failedJobs.count) {
				this.setState(
					{
						showErrorLogsButton: true,
						heading: 'Processing completed',
						description:
							(failedJobs.count || 0) === 1
								? `${failedJobs.count || 0} item was not added to the list • ${failedJobs.count || 0} error`
								: `${failedJobs.count || 0} items were not added to the list • ${failedJobs.count || 0} errors`,
					},
					() => {
						this.props.setUploadFileAction(true, this.props.spjAction, UploadFileActions.FILE_ERRORS);
					}
				);
			} else {
				this.setState(
					{
						showErrorLogsButton: false,
						heading: 'Processing completed',
						description: `All items were added to the list`,
					},
					() => {
						this.props.setUploadFileBatch('');
						this.props.setUploadFileAction(true, this.props.spjAction, UploadFileActions.SUCCESS);
					}
				);
			}
		}
	};

	downloadLogsHandler = async () => {
		const { batchId } = this.state;
		await this.props.downloadSPJErrors(this.props.spjAction, batchId, (err) => {
			this.showFileUploadError(err.message);
			this.setState({ showErrorLogs: false });
		});
	};

	clearLogsHandler = async () => {
		const { batchId } = this.state;
		await this.props.clearSPJFailed(batchId);
		this.onSuccessHandler();
	};

	onSuccessHandler = async () => {
		this.stopCheckingBatch();
		this.props.setUploadFileBatch('');
		this.props.setUploadFileAction(false);
		this.props.onSuccess();
		this.onCloseHandler();
	};

	onCloseHandler = () => {
		this.setState({ showErrorLogs: false });
		this.props.onClose();
	};

	showFileUploadSuccess = (resp) => {
		if (resp && resp.processedItems) {
			this.setState(
				{
					heading: 'Processing your uploaded file',
					description: (resp.processedItems || 0) === 1 ? `${resp.processedItems || 0} item remaining` : `${resp.processedItems || 0} items remaining`,
				},
				() => {
					this.props.setUploadFileBatch(resp.batch_id);
				}
			);
		}
	};

	showFileUploadError = (error) => {
		this.setState(
			{
				showErrorLogsButton: false,
				heading: 'Some error occured',
				description: error || '',
			},
			() => {
				this.props.setUploadFileAction(true, this.props.spjAction, UploadFileActions.ERROR);
			}
		);
	};

	downloadTemplateFile = async () => {
		this.setState({ downloadingTemplate: true });
		await this.props.downloadTemplateFile(this.props.spjAction, (err) => this.showFileUploadError(err.message));
		this.setState({ downloadingTemplate: false });
	};

	uploadFilledTemplateFile = async (files = null) => {
		if (files && files[0]) {
			let file = files[0];
			let formData = new FormData();
			formData.append('file', file, `${this.props.spjAction}-upload.xlsx`);

			this.setState(
				{
					heading: 'Uploading file',
					description: 'Wait while the file is being uploaded.',
				},
				async () => {
					this.props.setUploadFileAction(true, this.props.spjAction, UploadFileActions.PROCESSING);
					await this.props.uploadFilledTemplateFile(
						this.props.spjAction,
						formData,
						(resp) => this.showFileUploadSuccess(resp),
						(err) => this.showFileUploadError(err.message)
					);
				}
			);
		} else {
			this.showFileUploadError('File not found');
		}
	};

	render() {
		let { downloadingTemplate, showErrorLogs, showErrorLogsButton, heading, description } = this.state;
		let { open, title, children, uploadFileAction } = this.props;

		return (
			<Container open={open}>
				<Helmet>
					<title>{`Seller Portal | ${title}`}</title>
				</Helmet>

				<ErrorLogs
					open={showErrorLogs}
					downloadLogs={async () => {
						await this.downloadLogsHandler();
					}}
					clearLogs={async () => {
						await this.clearLogsHandler();
					}}
					onClose={() => {
						this.setState({ showErrorLogs: false });
					}}
				/>

				<ContainerHeading>
					{uploadFileAction === UploadFileActions.UPLOAD || uploadFileAction === UploadFileActions.PROCESSING || uploadFileAction === UploadFileActions.FILE_ERRORS ? (
						<ButtonGroup alignRight>
							<Button
								secondary
								outline
								onClick={(e) => {
									this.onCloseHandler();
								}}
							>
								Close
							</Button>
						</ButtonGroup>
					) : null}

					{uploadFileAction === UploadFileActions.ERROR ? (
						<ButtonGroup alignRight>
							<Button
								secondary
								outline
								onClick={(e) => {
									this.props.setUploadFileAction(false);
									this.onCloseHandler();
								}}
							>
								Close
							</Button>
						</ButtonGroup>
					) : null}
				</ContainerHeading>

				<ContainerBody>
					<FlexRow
						responsive='1200px'
						style={{ justifyContent: 'center', width: '100%' }}
					>
						{uploadFileAction === UploadFileActions.UPLOAD ? (
							<FlexColumn style={{ flex: 1, maxWidth: '1200px', lineHeight: '20px', fontSize: '14px' }}>
								<Title>
									<AddFileIcon />
									{`${title}`}
								</Title>

								<Description>
									<FlexColumn style={{ width: '100%' }}>{children}</FlexColumn>
								</Description>

								<Actions>
									<ButtonWithLoader
										secondary
										outline
										loading={downloadingTemplate}
										disabled={this.props.disableDownload || false}
										onClick={() => this.downloadTemplateFile()}
									>
										Download Template
									</ButtonWithLoader>
									<FileUpload
										name={`${this.props.spjAction}`}
										secondary
										label='Upload Filled XLSX'
										accept={'.xlsx,.xls,.numbers'}
										disabled={this.props.disableUpload || false}
										onInput={(e) => {
											this.uploadFilledTemplateFile(e.target.files, e);
										}}
									/>
								</Actions>
							</FlexColumn>
						) : null}

						{uploadFileAction === UploadFileActions.PROCESSING ? (
							<FlexColumn style={{ flex: 1, maxWidth: '1200px', lineHeight: '20px', fontSize: '14px' }}>
								<Title
									bgcolor='rgb(255, 240, 179, 0.3)'
									bordercolor='rgb(255, 209, 26)'
								>
									<ProcessingIcon />
									{heading || 'Fetching progress'}
								</Title>

								<Description>
									<FlexColumn>
										<FlexRow style={{ height: '10rem', background: '' }}>
											<CircularProgress showSpinner={true} />
										</FlexRow>

										<FlexRow style={{ justifyContent: 'center', textAlign: 'center', paddingTop: '0.5rem' }}>
											{description || 'Please refresh the page if it takes more than a minute.'}
										</FlexRow>
									</FlexColumn>
								</Description>
							</FlexColumn>
						) : null}

						{uploadFileAction === UploadFileActions.SUCCESS ? (
							<FlexColumn style={{ flex: 1, maxWidth: '1200px', lineHeight: '20px', fontSize: '14px' }}>
								<Title
									bgcolor='rgb(193, 240, 193, 0.3)'
									bordercolor='rgb(45, 185, 45)'
								>
									<SuccessIcon />
									{heading || 'Processing completed'}
								</Title>

								<Description>
									<FlexColumn>
										<FlexRow style={{ padding: '2rem 0', justifyContent: 'center' }}>
											<AddFileIcon style={{ fontSize: '4rem' }} />
											<ArrowRightIcon style={{ fontSize: '4rem' }} />
											<DatabaseIcon style={{ fontSize: '4rem' }} />
										</FlexRow>

										<FlexRow>{description || 'Please refresh the lists to sync with changes.'}</FlexRow>
									</FlexColumn>
								</Description>

								<Actions>
									<Button
										success
										onClick={() => this.onSuccessHandler()}
									>
										Refresh List
									</Button>
								</Actions>
							</FlexColumn>
						) : null}

						{uploadFileAction === UploadFileActions.ERROR ? (
							<FlexColumn style={{ flex: 1, maxWidth: '1200px', lineHeight: '20px', fontSize: '14px' }}>
								<Title
									bgcolor='rgb(255, 179, 179, 0.3)'
									bordercolor='rgb(255, 102, 102)'
								>
									<FailedIcon />
									{heading || 'Processing Failed'}
								</Title>

								<Description>
									<FlexColumn>
										<FlexRow style={{ padding: '2rem 0', justifyContent: 'center' }}>
											<ErrorIcon style={{ fontSize: '4rem' }} />
										</FlexRow>

										<FlexRow>{description || 'Some error occured'}</FlexRow>
									</FlexColumn>
								</Description>
							</FlexColumn>
						) : null}

						{uploadFileAction === UploadFileActions.FILE_ERRORS ? (
							<FlexColumn style={{ flex: 1, maxWidth: '1200px', lineHeight: '20px', fontSize: '14px' }}>
								<Title
									bgcolor='rgb(255, 179, 179, 0.3)'
									bordercolor='rgb(255, 102, 102)'
								>
									<FailedIcon />
									{heading || 'Fetching errors'}
								</Title>

								<Description>
									<FlexColumn>
										<FlexRow style={{ padding: '2rem 0', justifyContent: 'center' }}>
											{showErrorLogsButton ? <ErrorIcon style={{ fontSize: '4rem' }} /> : <CircularProgress showSpinner={true} />}
										</FlexRow>

										<FlexRow style={{ justifyContent: 'center', textAlign: 'center', paddingTop: '0.5rem' }}>
											{description || 'Please refresh the page if it takes more than a minute.'}
										</FlexRow>
									</FlexColumn>
								</Description>

								{showErrorLogsButton ? (
									<Actions>
										<Button
											error
											onClick={() => {
												this.setState({ showErrorLogs: true });
											}}
										>
											Show Errors
										</Button>
									</Actions>
								) : null}
							</FlexColumn>
						) : null}
					</FlexRow>
				</ContainerBody>
			</Container>
		);
	}
}

const mapStateToProps = (state, ownProps) => {
	return {
		children: ownProps.children || null,
	};
};

const mapDispatch = {
	fetchSPJBatchJobs,
	fetchSPJBatchMetric,
	clearSPJFailed,
	downloadSPJErrors,
	updateError,
	updateErrorMessage,
	updateMessage,
	updateSuccessMessage,
};

export default withRouter(connect(mapStateToProps, mapDispatch)(BulkUpload));
